// ===========================================
// Create Invoice Dialog
// ===========================================
// Dialog for creating a single manual invoice. Two modes:
//  - "Bill by week" (default once a term is chosen): pick lessons/students and
//    the weeks to bill per bucket. The SERVER prices them from the pricing
//    matrix (or a bucket's switched-on custom price) and words the lines — the
//    dialog previews the server's lines and submits only the selection
//    (POST /invoices/admin/invoices/from-weeks). Honours the school's
//    invoiceLineItemGrouping (BY_CLASS / BY_PROGRAM_LEVEL) on the server.
//  - Free-text line items (the original behaviour) for ad-hoc / non-lesson fees.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useCreateInvoice,
  useCreateInvoiceFromWeeks,
  useBillableContext,
  useWeekInvoicePreview,
} from '../../hooks/useInvoices';
import { useSnackbar } from 'notistack';
import { useUpdateEnrollment } from '../../hooks/useLessons';
import { useHistoryEntryState } from '../../hooks/useHistoryEntryState';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { getMissingPrices } from './missingPrices';
import ClassPricingLink from './ClassPricingLink';
import MissingPriceItem from './MissingPriceItem';
import { useInvoiceSettings } from '../../hooks/useAdminSettings';
import { Term } from '../../api/admin.api';
import { Family } from '../../api/users.api';
import {
  CreateInvoiceItemInput,
  InvoiceLineKind,
  InvoiceToAmend,
  WeekInvoicePreviewInput,
} from '../../api/invoices.api';
import { effectiveStartWeek } from '../../utils/dateUtils';
import { useDefaultDueDateField } from './useDefaultDueDateField';
import LineItemWeekRow from './LineItemWeekRow';
import LineKindSelect from './LineKindSelect';
import WeekItemsPreview from './WeekItemsPreview';
import InvoicesToAmendDialog, { type InvoicesToAmendContext } from './InvoicesToAmendDialog';
import WindowHasDataDialog, {
  parseWindowHasData,
  type WindowHasDataInfo,
} from '../admin/lessons/WindowHasDataDialog';
import {
  SelectedEnrollment,
  hasUnpricedBucket as selectionsHaveUnpricedBucket,
  overlongItems,
  seedSelection,
  startWeekWriteBack,
  storedEffectiveStart,
  toWeekSelectionPayload,
} from './lineItemWeekBuilder';

/**
 * A prefill (the "already invoiced" prompt's "Create an invoice for {name}", via the
 * `?create=1&familyId=…&termId=…&studentId=…&enrollmentId=…` deep link): the family, term and
 * "Bill specific weeks" are set at once; the student and their enrolment row only once the
 * billable context has LOADED (the family/term effect resets the selections before that).
 */
export interface CreateInvoiceInitial {
  familyId: string;
  termId: string;
  studentId?: string;
  enrollmentId?: string;
}

interface CreateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  terms: Term[];
  families: Family[];
  /** Applied ONCE per object while open. */
  initial?: CreateInvoiceInitial | null;
}

interface InvoiceLineItem extends Omit<CreateInvoiceItemInput, 'id' | 'kind'> {
  /** React key only: a new invoice's lines have no server id yet. */
  id: string;
  /** #372: what the line is for (default Other). */
  kind: InvoiceLineKind;
}

const newLine = (): InvoiceLineItem => ({
  id: crypto.randomUUID(),
  description: '',
  quantity: 1,
  unitPrice: 0,
  kind: 'OTHER',
});

/** The "no prompts" value for the history-entry state (never stored). */
const EMPTY_AMEND_PROMPTS: Array<{ invoices: InvoiceToAmend[]; context: InvoicesToAmendContext }> = [];

export default function CreateInvoiceDialog({
  open,
  onClose,
  terms,
  families,
  initial = null,
}: CreateInvoiceDialogProps) {
  const [familyId, setFamilyId] = useState('');
  const [termId, setTermId] = useState('');
  const [description, setDescription] = useState('');
  // Prefilled with the school's default (#243); a typed date is kept.
  const { dueDate, changeDueDate, resetDueDate, helperText: dueDateHelperText, isError: dueDateDefaultFailed } =
    useDefaultDueDateField(open);
  // Off by default — don't presume the invoice is lesson/week-based; the admin
  // opts in. Free-text line items are the default entry mode.
  const [billByWeek, setBillByWeek] = useState(false);
  // Week-mode: which students' lessons we're billing (a family may have several;
  // e.g. two siblings who started mid-term on the same invoice). Multi-select.
  const [studentIds, setStudentIds] = useState<string[]>([]);
  // Week-mode selections, keyed by enrollmentId.
  const [selections, setSelections] = useState<Record<string, SelectedEnrollment>>({});
  const [addEnrollmentId, setAddEnrollmentId] = useState('');
  // Free-text fallback items.
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([newLine()]);

  // After a week-mode invoice is created, rows whose "Save as {student}'s start week" box was
  // ticked write the chosen start back to the enrolment. The dialog has closed by then, so the
  // answers (a refusal, or invoices that may need amending) are shown from here.
  const updateEnrollment = useUpdateEnrollment();
  const { enqueueSnackbar } = useSnackbar();
  const [startWeekRefusals, setStartWeekRefusals] = useState<WindowHasDataInfo[]>([]);
  // Kept with this history entry, so Back from an invoice brings the list back (D-c).
  const [amendPrompts, setAmendPrompts] = useHistoryEntryState<
    Array<{ invoices: InvoiceToAmend[]; context: InvoicesToAmendContext }>
  >('create-invoice-amend-prompts', EMPTY_AMEND_PROMPTS);

  const createMutation = useCreateInvoice();
  const createFromWeeksMutation = useCreateInvoiceFromWeeks();
  const { data: invoiceSettings } = useInvoiceSettings();
  const grouping = invoiceSettings?.invoiceLineItemGrouping ?? 'BY_CLASS';

  const weekModeActive = billByWeek && !!familyId && !!termId;

  const {
    data: context,
    isLoading: contextLoading,
    isError: contextError,
    error: contextErrorObj,
  } = useBillableContext(
    weekModeActive ? familyId : undefined,
    weekModeActive ? termId : undefined
  );

  // Reset week selections + students whenever the family or term changes.
  useEffect(() => {
    setSelections({});
    setAddEnrollmentId('');
    setStudentIds([]);
  }, [familyId, termId]);

  // ---- Prefill (`initial`) ----
  // Step 1, once per `initial` while open: family, term, week mode. Step 2 (below the
  // auto-select effects) waits for the billable context of THAT family and term, because the
  // reset effect above clears the selections whenever the family or term changes.
  const appliedInitialRef = useRef<CreateInvoiceInitial | null>(null);
  const pendingRowRef = useRef<CreateInvoiceInitial | null>(null);
  const [prefillNotice, setPrefillNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !initial || appliedInitialRef.current === initial) return;
    appliedInitialRef.current = initial;
    const familyKnown = families.some((f) => f.id === initial.familyId);
    const termKnown = terms.some((t) => t.id === initial.termId);
    setBillByWeek(true);
    if (familyKnown) setFamilyId(initial.familyId);
    if (termKnown) setTermId(initial.termId);
    if (familyKnown && termKnown) {
      pendingRowRef.current = initial;
      setPrefillNotice(null);
    } else {
      pendingRowRef.current = null;
      setPrefillNotice(
        !familyKnown
          ? "That family isn't in the list. Choose the family and term, then the student's class."
          : "That term isn't in the list. Choose the term, then the student's class."
      );
    }
  }, [open, initial, families, terms]);

  // Distinct students that have enrolments in this family+term (preserve order).
  const students = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of context?.enrollments ?? []) {
      if (!seen.has(e.studentId)) seen.set(e.studentId, e.studentName);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [context]);

  // Auto-select the only student so the admin doesn't have to pick. When several,
  // they choose (one or more). Drop any selected id no longer in the loaded set.
  useEffect(() => {
    if (students.length === 1) {
      setStudentIds([students[0].id]);
    } else {
      setStudentIds((prev) => prev.filter((id) => students.some((s) => s.id === id)));
    }
  }, [students]);

  // Deselecting a student removes their already-added rows — otherwise weeks for a
  // student the admin just un-ticked would still bill (a surprising footgun).
  useEffect(() => {
    setSelections((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([, sel]) => studentIds.includes(sel.enrollment.studentId))
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [studentIds]);

  // Prefill step 2: declared AFTER the reset / auto-select effects so its state updates land
  // last in the same commit. Runs once the context for the prefilled family and term is here.
  useEffect(() => {
    const pending = pendingRowRef.current;
    if (!pending) return;
    // A context that fails (e.g. the missing-prices 422) ends the prefill: its own error shows,
    // and the row must not appear later when the admin returns to this family and term.
    if (contextError && familyId === pending.familyId && termId === pending.termId) {
      pendingRowRef.current = null;
      return;
    }
    if (!context || contextLoading) return;
    if (familyId !== pending.familyId || termId !== pending.termId || context.termId !== pending.termId) return;
    pendingRowRef.current = null;
    // A NAMED enrolment is matched exactly, never by student: the student's other class is
    // usually the one already on the family's invoice, and pre-adding it would double-bill.
    const enrollment = pending.enrollmentId
      ? context.enrollments.find((e) => e.enrollmentId === pending.enrollmentId)
      : context.enrollments.find((e) => e.studentId === pending.studentId);
    if (!enrollment) {
      setPrefillNotice(
        "That student's class isn't on this family's billable list for the term. Choose the student and class below."
      );
      return;
    }
    setStudentIds((prev) => (prev.includes(enrollment.studentId) ? prev : [...prev, enrollment.studentId]));
    // The add path's own seeding: a stored start week seeds the Starting week; none = empty.
    setSelections({ [enrollment.enrollmentId]: seedSelection(enrollment, context.termWeeks ?? null) });
  }, [context, contextLoading, contextError, familyId, termId]);

  // Enrollments for the chosen student(s), not yet added as rows (the "add lesson"
  // picker). Empty until at least one student is selected (multi-student families).
  const availableEnrollments = useMemo(
    () =>
      (context?.enrollments ?? []).filter(
        (e) => studentIds.includes(e.studentId) && !selections[e.enrollmentId]
      ),
    [context, selections, studentIds]
  );

  const handleAddEnrollment = () => {
    const enrollment = context?.enrollments.find((e) => e.enrollmentId === addEnrollmentId);
    if (!enrollment) return;
    setSelections((prev) => ({
      ...prev,
      // termWeeks bounds a start-week click to weeks the picker actually shows.
      [enrollment.enrollmentId]: seedSelection(enrollment, context?.termWeeks ?? null),
    }));
    setAddEnrollmentId('');
  };

  const handleRemoveEnrollment = (enrollmentId: string) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[enrollmentId];
      return next;
    });
  };

  // Block submit if ANY selected bucket (weeks chosen) has no price to bill at —
  // no matrix price and no custom price switched on. Checked on the selections,
  // not the built lines: under BY_PROGRAM_LEVEL an unpriced bucket would hide
  // inside a merged sum. (The server refuses it too — 422.)
  const hasUnpricedBucket = selectionsHaveUnpricedBucket(Object.values(selections));

  // What the server prices: weeks + starting week + switched-on custom prices.
  const weekSelectionPayload = useMemo(
    () => toWeekSelectionPayload(Object.values(selections)),
    [selections]
  );
  const previewInput: WeekInvoicePreviewInput | null = useMemo(
    () =>
      weekModeActive && weekSelectionPayload.length > 0 && !hasUnpricedBucket
        ? { familyId, termId, selections: weekSelectionPayload }
        : null,
    [weekModeActive, weekSelectionPayload, hasUnpricedBucket, familyId, termId]
  );
  // Debounced so typing a custom price is one request, not one per keystroke.
  const debouncedPreviewInput = useDebouncedValue(previewInput, 300);
  const preview = useWeekInvoicePreview(debouncedPreviewInput);
  // The preview is CURRENT only when it answers exactly what is on screen — a
  // stale one (a click still in flight) must not enable Create.
  const previewCurrent =
    previewInput !== null &&
    debouncedPreviewInput === previewInput &&
    !!preview.data &&
    !preview.isPlaceholderData &&
    !preview.isFetching &&
    !preview.isError;
  // The SERVER's lines — the exact text and prices that will be created.
  const weekItems: CreateInvoiceItemInput[] =
    previewInput !== null && preview.data ? preview.data.items : [];
  const startDates = previewCurrent ? preview.data?.startDates : undefined;

  /**
   * Write each ticked row's start week back to its enrolment, one after another. A failure
   * NEVER touches the invoice (already created): it is reported and the rest carry on.
   */
  const writeBackStartWeeks = async (
    rows: Array<{ sel: SelectedEnrollment; startWeek: number | null }>,
    termName: string,
    createdInvoiceId?: string
  ) => {
    for (const { sel, startWeek } of rows) {
      const { enrollment } = sel;
      try {
        const result = await updateEnrollment.mutateAsync({
          lessonId: enrollment.lessonId,
          studentId: enrollment.studentId,
          startWeek,
          silent: true,
        });
        enqueueSnackbar(`${enrollment.studentName.split(' ')[0]}'s start week saved`, { variant: 'success' });
        // The invoice this dialog JUST created is a draft for the same family and term, so the
        // lookup lists it — but it already bills from the chosen week. Never offer it.
        const invoices = (result?.invoicesToAmend ?? []).filter((invoice) => invoice.id !== createdInvoiceId);
        if (invoices.length > 0) {
          const prompt = {
            invoices,
            context: {
              kind: 'student' as const,
              lessonId: enrollment.lessonId,
              studentId: enrollment.studentId,
              studentName: enrollment.studentName,
              lessonName: enrollment.lessonName,
              termName,
              newStartWeek: effectiveStartWeek({ startWeek: enrollment.classStartWeek }, { startWeek }),
              previousStartWeek: storedEffectiveStart(enrollment),
            },
          };
          setAmendPrompts((prev) => [...prev, prompt]);
        }
      } catch (err) {
        const refused = parseWindowHasData(err);
        if (refused) {
          setStartWeekRefusals((prev) => [...prev, refused]);
        } else {
          const reason =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            (err as Error)?.message ??
            'Unknown error';
          enqueueSnackbar(
            `Invoice created. Couldn't save ${enrollment.studentName.split(' ')[0]}'s start week: ${reason}`,
            { variant: 'warning', autoHideDuration: 12000 }
          );
        }
      }
    }
  };

  const handleCreate = () => {
    // Both create routes require a date; never invent one in the browser (#243).
    if (!familyId || !dueDate) return;

    const dueDateISO = new Date(dueDate).toISOString();

    if (weekModeActive) {
      // Send the SELECTION, never prices — the server prices it from the matrix.
      if (weekSelectionPayload.length === 0) return;
      // Snapshot the ticked rows now: closing the dialog resets the selections.
      const billed = new Set(weekSelectionPayload.map((p) => p.enrollmentId));
      const writeBacks = Object.values(selections)
        .filter((sel) => sel.saveStartWeek === true && billed.has(sel.enrollment.enrollmentId))
        .map((sel) => ({ sel, ...startWeekWriteBack(sel) }))
        .filter((row) => row.changed)
        .map(({ sel, startWeek }) => ({ sel, startWeek }));
      const termName = context?.termName ?? 'this term';
      createFromWeeksMutation.mutate(
        {
          familyId,
          termId,
          description: description || undefined,
          dueDate: dueDateISO,
          selections: weekSelectionPayload,
        },
        {
          onSuccess: (created) => {
            handleClose();
            if (writeBacks.length > 0) void writeBackStartWeeks(writeBacks, termName, created?.id);
          },
        }
      );
      return;
    }

    const items = lineItems
      .filter((i) => i.description.trim() && i.quantity > 0 && i.unitPrice > 0)
      // #372: the admin's kind goes with each typed line.
      .map(({ description, quantity, unitPrice, kind }) => ({ description, quantity, unitPrice, kind }));
    if (items.length === 0) return;

    createMutation.mutate(
      {
        familyId,
        termId: termId || undefined,
        description: description || undefined,
        dueDate: dueDateISO,
        items,
      },
      { onSuccess: () => handleClose() }
    );
  };

  const handleClose = () => {
    setFamilyId('');
    setTermId('');
    setDescription('');
    resetDueDate();
    setBillByWeek(false);
    setStudentIds([]);
    setSelections({});
    setAddEnrollmentId('');
    setLineItems([newLine()]);
    setPrefillNotice(null);
    pendingRowRef.current = null;
    onClose();
  };

  const handleAddLineItem = () => {
    setLineItems([...lineItems, newLine()]);
  };

  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length > 1) setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const handleLineItemChange = (
    id: string,
    field: 'description' | 'quantity' | 'unitPrice' | 'kind',
    value: string | number
  ) => {
    setLineItems(lineItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  const freeTextTotal = lineItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
    0
  );
  const weekTotal = previewInput !== null && preview.data ? preview.data.total : 0;

  // The backend rejects a description over 200 characters; a start date and a
  // merged summary make that reachable, so block here with a named reason.
  const hasOverlongItem = overlongItems(weekItems).length > 0;

  // Whichever create path is in use (week selection vs free-text lines).
  const activeMutation = weekModeActive ? createFromWeeksMutation : createMutation;

  const isValid = !!dueDate && (weekModeActive
    ? !!familyId && previewCurrent && weekItems.length > 0 && !hasUnpricedBucket && !hasOverlongItem
    : !!familyId &&
      lineItems.some((i) => i.description.trim() && i.quantity > 0 && i.unitPrice > 0));

  return (
    <Fragment>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Invoice</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {prefillNotice && (
            <Alert severity="info" data-testid="create-invoice-prefill-notice">
              {prefillNotice}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 1 }}>
            Create a manual invoice for a family. The invoice will be saved as a draft and can be
            sent when ready.
          </Alert>

          {/* Family Selection */}
          <FormControl fullWidth required>
            <InputLabel>Family</InputLabel>
            <Select
              value={familyId}
              label="Family"
              onChange={(e: SelectChangeEvent) => setFamilyId(e.target.value)}
            >
              {families.map((family) => (
                <MenuItem key={family.id} value={family.id}>
                  {family.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Term Selection */}
          <FormControl fullWidth>
            <InputLabel>Term{billByWeek ? '' : ' (Optional)'}</InputLabel>
            <Select
              value={termId}
              label={`Term${billByWeek ? '' : ' (Optional)'}`}
              onChange={(e: SelectChangeEvent) => setTermId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {terms.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Mode toggle */}
          <FormControlLabel
            control={
              <Switch checked={billByWeek} onChange={(e) => setBillByWeek(e.target.checked)} />
            }
            label="Bill specific weeks (auto-priced from enrolments)"
          />
          {billByWeek && !termId && (
            <Alert severity="warning">Select a term to choose weeks to bill.</Alert>
          )}

          {/* Description */}
          <TextField
            fullWidth
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Music lessons - Term 1 2026"
          />

          {/* Due Date */}
          <TextField
            fullWidth
            type="date"
            label="Due Date"
            value={dueDate}
            onChange={(e) => changeDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            error={dueDateDefaultFailed && !dueDate}
            helperText={dueDateHelperText}
          />

          <Divider sx={{ my: 1 }} />

          {/* ---- WEEK MODE ---- */}
          {weekModeActive ? (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Lessons & Weeks
              </Typography>

              {contextLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {contextError && (() => {
                const missing = getMissingPrices(contextErrorObj);
                if (missing) {
                  return (
                    <Alert severity="error">
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        These prices are missing (or $0) in the pricing matrix — add them before billing:
                      </Typography>
                      <List dense disablePadding sx={{ mb: 0.5 }}>
                        {missing.map((m) => (
                          <ListItem key={`${m.lessonId}-${m.durationMinutes}-${m.category}`} sx={{ py: 0 }}>
                            <ListItemText primary={<MissingPriceItem missing={m} />} />
                          </ListItem>
                        ))}
                      </List>
                      {missing.some((m) => !m.noProgramLevel) && <ClassPricingLink />}
                    </Alert>
                  );
                }
                return (
                  <Alert severity="error">Failed to load enrolments for this family and term.</Alert>
                );
              })()}
              {context && context.enrollments.length === 0 && (
                <Alert severity="info">
                  No active enrolments for this family in the selected term.
                </Alert>
              )}

              {/* Student picker — only when the family has more than one student
                  with enrolments. A single student is auto-selected. */}
              {students.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Students</InputLabel>
                  <Select
                    multiple
                    value={studentIds}
                    onChange={(e) =>
                      setStudentIds(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    input={<OutlinedInput label="Students" />}
                    renderValue={(selected) =>
                      students
                        .filter((s) => selected.includes(s.id))
                        .map((s) => s.name)
                        .join(', ')
                    }
                  >
                    {students.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Checkbox checked={studentIds.includes(s.id)} />
                        <ListItemText primary={s.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {students.length > 1 && studentIds.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select one or more students to bill.
                </Alert>
              )}

              {/* Add-lesson picker */}
              {availableEnrollments.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Add a lesson</InputLabel>
                    <Select
                      value={addEnrollmentId}
                      label="Add a lesson"
                      onChange={(e: SelectChangeEvent) => setAddEnrollmentId(e.target.value)}
                    >
                      {availableEnrollments.map((e) => (
                        <MenuItem key={e.enrollmentId} value={e.enrollmentId}>
                          {e.studentName} — {e.lessonName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddEnrollment}
                    disabled={!addEnrollmentId}
                  >
                    Add
                  </Button>
                </Box>
              )}

              {/* Selected enrollment rows */}
              {context &&
                Object.values(selections).map((sel) => (
                  <LineItemWeekRow
                    key={sel.enrollment.enrollmentId}
                    enrollment={sel.enrollment}
                    termStartDate={context.termStartDate}
                    termEndDate={context.termEndDate}
                    value={sel}
                    onChange={(next) =>
                      setSelections((prev) => ({ ...prev, [sel.enrollment.enrollmentId]: next }))
                    }
                    onRemove={() => handleRemoveEnrollment(sel.enrollment.enrollmentId)}
                    startDate={startDates?.[sel.enrollment.enrollmentId]}
                    // Offered in a LOCKED term too: a start week stays editable after the term
                    // locks so its invoices can be corrected (2026-10-02).
                    allowSaveStartWeek
                  />
                ))}

              {Object.keys(selections).length === 0 && context && context.enrollments.length > 0 && (
                <Alert severity="info">Add a lesson above to start billing weeks.</Alert>
              )}

              {/* The SERVER's lines, worded and priced exactly as they will be created */}
              <WeekItemsPreview
                items={weekItems}
                caption={grouping === 'BY_PROGRAM_LEVEL' ? 'grouped by program level' : 'one per class'}
                updating={previewInput !== null && !previewCurrent && !preview.isError}
              />
              {preview.isError && previewInput !== null && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {(preview.error as Error)?.message || 'Could not price the selected weeks'}
                </Alert>
              )}

              {hasUnpricedBucket && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  One or more lessons have no price set (no matching price in the pricing matrix).
                  Enter a price per week before creating the invoice.
                </Alert>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6">Total: {formatCurrency(weekTotal)}</Typography>
              </Box>
            </Box>
          ) : (
            /* ---- FREE-TEXT MODE ---- */
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Line Items
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddLineItem}>
                  Add Item
                </Button>
              </Box>

              {lineItems.map((item) => (
                <Grid container spacing={1} key={item.id} sx={{ mb: 1, alignItems: 'center' }}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                      placeholder="e.g., Piano classes"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    {/* #372: what the line is for (decides which invoices a late fee charges). */}
                    <LineKindSelect value={item.kind} onChange={(kind) => handleLineItemChange(item.id, 'kind', kind)} />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Qty"
                      value={item.quantity}
                      onChange={(e) =>
                        handleLineItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)
                      }
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Price ($)"
                      value={item.unitPrice || ''}
                      onChange={(e) =>
                        handleLineItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)
                      }
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="0.00"
                    />
                  </Grid>
                  <Grid item xs={3} sm={2}>
                    <Typography variant="body2" sx={{ textAlign: 'right' }}>
                      {formatCurrency((item.quantity || 0) * (item.unitPrice || 0))}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveLineItem(item.id)}
                      disabled={lineItems.length === 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6">Total: {formatCurrency(freeTextTotal)}</Typography>
              </Box>
            </Box>
          )}

          {activeMutation.isError && (
            <Alert severity="error">
              {(activeMutation.error as Error)?.message || 'Failed to create invoice'}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={activeMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!isValid || activeMutation.isPending}
          startIcon={
            activeMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {activeMutation.isPending ? 'Creating...' : 'Create Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
    <WindowHasDataDialog
      info={startWeekRefusals[0] ?? null}
      onClose={() => setStartWeekRefusals((prev) => prev.slice(1))}
      note="The invoice was created. Only the starting week was not saved."
    />
    {amendPrompts[0] && (
      <InvoicesToAmendDialog
        open
        onClose={() => setAmendPrompts((prev) => prev.slice(1))}
        invoices={amendPrompts[0].invoices}
        context={amendPrompts[0].context}
      />
    )}
    </Fragment>
  );
}
